import { useTranslation } from 'react-i18next';
import { HStack, Box } from 'native-base';
import moment from 'moment';

import { NumberText, Text } from '@pimm/base';
import { useSiteTime } from '@app/features/store-core';
import { LAYOUT } from '../consts';
import { SalesBlock, SalesDaypart } from '../types';
import { getLaborVarianceColors } from '../_helper';

export type LaborRow = {
  key: string;
  label: string;
  getValue: (slot: SalesBlock) => number | null;
  isBold?: boolean;
  isGroupHeader?: boolean;
  isIndented?: boolean;
  isVariance?: boolean;
  hasTopBorder?: boolean;
  hideWhenNotCompleted?: boolean;
};

type LaborTableRowProps = {
  row: LaborRow;
  tableData?: Partial<SalesDaypart>;
};

export const LaborTableRow = ({ row, tableData }: LaborTableRowProps) => {
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const now = siteTime.today();

  return (
    <HStack alignItems="stretch" borderTopWidth={row.hasTopBorder ? 2 : 0} borderTopColor="gray.200">
      {/* Row label */}
      <Box
        w={LAYOUT.stickyLabelW}
        pr={2.5}
        pl={row.isIndented ? 5 : 2.5}
        py={1}
        minH="22px"
        justifyContent="center"
        borderRightWidth={1}
        borderColor="gray.200"
      >
        <Text
          size={{ base: 'xs', xl: 'sm' }}
          fontWeight={row.isBold ? 600 : 400}
          color={row.isIndented ? 'gray.800' : 'gray.900'}
          flexShrink={1}
          lineHeight="xs"
        >
          {t('sales_tracker:' + row.label)}
        </Text>
      </Box>

      {/* Data cells */}
      {tableData?.blocks?.map(block => {
        const value = row.getValue(block);
        const isNotCompleted = block.endTime > now;
        const isFuture = !block.hasActual;
        const isHourly = moment(block.endTime).diff(block.startTime, 'minutes') > 30;
        const showDash = isFuture || (isNotCompleted && row.hideWhenNotCompleted);
        let colors = getLaborVarianceColors(row.key, value ?? 0, isHourly);

        if (isNotCompleted) {
          colors = { bg: 'grayCool.50', text: 'gray.500' };
        }

        return (
          <HStack
            key={`labor-table.${row.key}.${block.startTime.toISOString()}`}
            flex={1}
            alignItems="center"
            justifyContent="center"
            p={0.5}
            borderLeftWidth={1}
            borderColor="gray.200"
            bg={colors.bg}
          >
            {showDash ? (
              <Text size={{ base: 'xs', xl: 'sm' }} color="gray.500">
                -
              </Text>
            ) : (
              <NumberText
                size={{ base: 'xs', xl: 'sm' }}
                fontWeight={row.isBold ? 600 : 400}
                color={colors.text}
                fixedDecimalScale
                value={value ?? 0}
              />
            )}
          </HStack>
        );
      })}
    </HStack>
  );
};
